<script setup>
import { computed } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  selectedProductTypeForAi: {
    type: String,
    default: ''
  },
  registrationTypes: {
    type: Array,
    required: true
  },
  loading: {
    type: Boolean,
    default: false
  },
  aiMappingResult: {
    type: Object,
    default: null
  },
  csvHeaders: {
    type: Array,
    default: () => []
  },
  showProductTypeDropdown: {
    type: Boolean,
    default: false
  },
  errorMessage: {
    type: String,
    default: ''
  },
  showError: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits([
  'select-product-type',
  'toggle-product-dropdown',
  'update-field-mapping',
  'retry-analysis'
]);

// Get required columns based on product type
const getRequiredColumns = (productType) => {
  const baseColumns = [
    { key: 'name', label: 'Nome', required: true },
    { key: 'description', label: 'Descrição', required: true },
    { key: 'price', label: 'Preço', required: true },
    { key: 'discountPrice', label: 'Preço com desconto', required: false },
    { key: 'discountReason', label: 'Motivo do desconto', required: false },
    { key: 'category', label: 'Categoria', required: false },
    { key: 'images', label: 'Imagens', required: false }
  ];
  
  const typeSpecificColumns = {
    'digital': [
      { key: 'contextUrl', label: 'URL de acesso', required: false },
      { key: 'fileFormat', label: 'Formato do arquivo', required: false },
      { key: 'licenseType', label: 'Tipo de licença', required: false }
    ],
    'physical': [
      { key: 'contextUrl', label: 'URL do produto', required: false },
      { key: 'weightKg', label: 'Peso (kg)', required: false },
      { key: 'height_cm', label: 'Altura (cm)', required: false },
      { key: 'width_cm', label: 'Largura (cm)', required: false },
      { key: 'depth_cm', label: 'Profundidade (cm)', required: false },
      { key: 'color', label: 'Cor', required: false },
      { key: 'shipsTo', label: 'Locais de envio', required: false }
    ],
    'delivery': [
      { key: 'link', label: 'Link', required: false },
      { key: 'address', label: 'Endereço', required: false },
      { key: 'maxDistance', label: 'Distância máxima', required: false },
      { key: 'isFixedPrice', label: 'Preço fixo', required: false },
      { key: 'estimatedMinutes', label: 'Tempo estimado (min)', required: false }
    ],
    'consulta': [
      { key: 'link', label: 'Link', required: false },
      { key: 'address', label: 'Endereço', required: false },
      { key: 'isHourlyRate', label: 'Taxa por hora', required: false },
      { key: 'advanceBookingDays', label: 'Dias de antecedência', required: false },
      { key: 'specialty', label: 'Especialidade', required: false },
      { key: 'durationMinutes', label: 'Duração (min)', required: false }
    ],
    'tratamento': [
      { key: 'link', label: 'Link', required: false },
      { key: 'address', label: 'Endereço', required: false },
      { key: 'isHourlyRate', label: 'Taxa por hora', required: false },
      { key: 'advanceBookingDays', label: 'Dias de antecedência', required: false },
      { key: 'specialty', label: 'Especialidade', required: false },
      { key: 'durationMinutes', label: 'Duração (min)', required: false }
    ],
    'aluguel': [
      { key: 'link', label: 'Link', required: false },
      { key: 'address', label: 'Endereço', required: false },
      { key: 'isHourlyRate', label: 'Taxa por hora', required: false },
      { key: 'advanceBookingDays', label: 'Dias de antecedência', required: false }
    ],
    'assinatura': [
      { key: 'link', label: 'Link', required: false },
      { key: 'address', label: 'Endereço', required: false },
      { key: 'planType', label: 'Tipo de plano', required: false }
    ]
  };
  
  const specificColumns = typeSpecificColumns[productType] || [];
  return [...baseColumns, ...specificColumns];
};

// Get the AI mapping for a specific template field
const getMappingForTemplateField = (fieldKey) => {
  if (!props.aiMappingResult) return null;
  
  const mapping = props.aiMappingResult.mappings.find(m => m.mapped_field === fieldKey && m.mapped_field !== 'unmapped');
  
  // Return null if no mapping found or if csv_header is null/empty (no actual column mapping)
  if (!mapping || mapping.csv_header === null || mapping.csv_header === '') {
    return null;
  }
  
  return mapping;
};

// Get available CSV columns for dropdown (excluding already mapped ones)
const getAvailableCsvColumns = (currentField) => {
  if (!props.csvHeaders || !props.aiMappingResult) return [];
  
  // Get all currently mapped CSV headers except for the current field
  const mappedHeaders = props.aiMappingResult.mappings
    .filter(m => m.mapped_field !== 'unmapped' && m.mapped_field !== currentField && m.csv_header !== null && m.csv_header !== '')
    .map(m => m.csv_header);
  
  // Return available headers (not already mapped)
  return props.csvHeaders.filter(header => !mappedHeaders.includes(header));
};

// Get current dropdown value for a template field
const getCurrentDropdownValue = (templateFieldKey) => {
  const mapping = getMappingForTemplateField(templateFieldKey);
  return mapping ? mapping.csv_header : 'unmapped';
};

const requiredColumns = computed(() => getRequiredColumns(props.selectedProductTypeForAi));
</script>

<template>
  <div>
    <!-- Product Type Selection -->
    <div v-if="!aiMappingResult && !loading && !showError" class="p-6">
      <div class="text-center py-10 border-2 border-dashed border-n-slate-3 dark:border-n-slate-7 rounded-lg bg-n-slate-1">
        <div class="flex flex-col items-center max-w-md mx-auto">
          <i class="i-lucide-sparkles text-5xl text-n-brand mb-3"></i>
          <h4 class="text-lg font-medium text-n-slate-12 mb-2">Selecione o tipo de produto</h4>
          <p class="text-sm text-n-slate-11 mb-6 text-center">
            Escolha o tipo de produto para que a IA possa mapear corretamente os campos da sua planilha
          </p>
          
          <!-- Product Type Dropdown -->
          <div class="w-full max-w-sm filter-actions">
            <div 
              class="rounded-lg button-group outline-1 outline outline-n-container w-full">
              <Button
                :label="selectedProductTypeForAi ? registrationTypes.find(t => t.value === selectedProductTypeForAi)?.label : 'Selecione o tipo de produto'"
                size="sm"
                color="slate"
                class="rounded-r-none !outline-0 bg-n-background dark:bg-n-slate-3 flex-1 min-w-0 !justify-start !h-12"
                @click="emit('toggle-product-dropdown', $event)"
              />
              <Button
                icon="i-lucide-chevron-down"
                size="sm"
                color="slate"
                class="rounded-l-none !outline-0 bg-n-background dark:bg-n-slate-3 !h-12"
                trailing-icon
                @click="emit('toggle-product-dropdown', $event)"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- AI Analysis Loading -->
    <div v-else-if="loading" class="p-6">
      <div class="bg-n-background rounded-lg border border-n-slate-3 dark:border-n-slate-7 p-6">
        <div class="flex flex-col items-center justify-center py-10 gap-4">
          <div class="w-16 h-16 bg-n-brand/10 rounded-full flex items-center justify-center animate-pulse">
            <i class="i-lucide-sparkles text-n-brand text-2xl"></i>
          </div>
          <div class="text-center">
            <h3 class="text-lg font-semibold text-n-slate-12 mb-2">
              Analisando planilha com IA
            </h3>
            <p class="text-n-slate-11 text-sm px-36">
              A IA está mapeando as colunas da sua planilha para os campos corretos. Isso pode levar alguns segundos.
            </p>
          </div>
          <div class="flex items-center gap-3 mt-4">
            <div class="w-2 h-2 bg-n-brand rounded-full animate-bounce" style="animation-delay: 0ms"></div>
            <div class="w-2 h-2 bg-n-brand rounded-full animate-bounce" style="animation-delay: 150ms"></div>
            <div class="w-2 h-2 bg-n-brand rounded-full animate-bounce" style="animation-delay: 300ms"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- AI Analysis Error -->
    <div v-else-if="showError" class="p-6">
      <div class="bg-n-background rounded-lg border border-n-slate-3 dark:border-n-slate-7 p-6">
        <div class="flex flex-col items-center justify-center py-10 gap-4">
          <div class="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center">
            <i class="i-lucide-alert-triangle text-red-500 text-2xl"></i>
          </div>
          <div class="text-center">
            <h3 class="text-lg font-semibold text-n-slate-12 mb-2">
              Erro no Mapeamento
            </h3>
            <p class="text-n-slate-11 text-sm px-36">
              {{ errorMessage || 'Houve um erro interno durante o mapeamento. Tente novamente ou envie um modelo pronto.' }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- AI Mapping Interface -->
    <div v-else-if="aiMappingResult" class="p-6">
      <div class="space-y-6">
        <!-- Mapping Table -->
        <div>
          <div class="space-y-2 max-h-80 overflow-y-auto">
            <!-- Show all template fields -->
            <div 
              v-for="templateField in requiredColumns" 
              :key="templateField.key"
              class="flex items-center gap-4 p-3 border rounded-lg"
              :class="{
                'bg-n-background': getMappingForTemplateField(templateField.key),
                'bg-n-background opacity-50 border-red-500': !getMappingForTemplateField(templateField.key) && templateField.required,
                'bg-n-background opacity-50': !getMappingForTemplateField(templateField.key) && !templateField.required,
                'border-n-slate-3 dark:border-n-slate-7': getMappingForTemplateField(templateField.key) || !templateField.required
              }"
            >
              <!-- Template Field (Left) -->
              <div class="flex-1 min-w-0">
                <p class="text-sm font-medium text-n-slate-12 truncate">
                  {{ templateField.label }}
                  <span v-if="templateField.required" class="text-red-500">*</span>
                </p>
                <p class="text-xs text-n-slate-11">{{ templateField.required ? 'Campo obrigatório' : 'Campo opcional' }}</p>
              </div>
              
              <!-- Arrow or X -->
              <i 
                class="flex-shrink-0"
                :class="{
                  'i-lucide-arrow-right text-n-slate-9': getMappingForTemplateField(templateField.key),
                  'i-lucide-x text-n-slate-9': !getMappingForTemplateField(templateField.key)
                }"
              ></i>
              
              <!-- User's CSV Column Dropdown (Right) -->
              <div class="flex-1 max-w-xs">
                <select 
                  :value="getCurrentDropdownValue(templateField.key)"
                  @change="emit('update-field-mapping', templateField.key, $event.target.value)"
                  class="w-full border border-n-slate-6 dark:border-n-slate-7 rounded-lg text-sm bg-n-background text-n-slate-12 focus:border-n-brand hover:border-n-slate-4 mb-1"
                >
                  <option value="unmapped">
                    Não mapeado
                  </option>
                  <option 
                    v-for="h in csvHeaders" 
                    :key="h" 
                    :value="h"
                  >
                    {{ h }}
                  </option>
                  <!-- Show currently mapped column even if it would be "unavailable" for others -->
                  <option 
                    v-if="getCurrentDropdownValue(templateField.key) !== 'unmapped' && !getAvailableCsvColumns(templateField.key).includes(getCurrentDropdownValue(templateField.key))"
                    :value="getCurrentDropdownValue(templateField.key)"
                  >
                    {{ getCurrentDropdownValue(templateField.key) }}
                  </option>
                </select>
                <p class="text-xs text-n-slate-11 mt-1">Coluna da planilha</p>
              </div>
              
              <!-- Confidence Indicator -->
              <div v-if="getMappingForTemplateField(templateField.key)" class="flex-shrink-0">
                <div 
                  class="w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium text-white"
                  :class="{
                    'bg-n-brand': getMappingForTemplateField(templateField.key).user_modified,
                    'bg-green-500': !getMappingForTemplateField(templateField.key).user_modified && getMappingForTemplateField(templateField.key).confidence >= 0.8,
                    'bg-yellow-500': !getMappingForTemplateField(templateField.key).user_modified && getMappingForTemplateField(templateField.key).confidence >= 0.5 && getMappingForTemplateField(templateField.key).confidence < 0.8,
                    'bg-red-500': !getMappingForTemplateField(templateField.key).user_modified && getMappingForTemplateField(templateField.key).confidence < 0.5
                  }"
                >
                  <i v-if="getMappingForTemplateField(templateField.key).user_modified" class="i-lucide-pen"></i>
                  <span v-else>{{ Math.round(getMappingForTemplateField(templateField.key).confidence * 100) }}%</span>
                </div>
              </div>
              <div v-else class="flex-shrink-0 w-8"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
@keyframes bounce {
  0%, 60%, 100% {
    transform: translateY(0);
  }
  30% {
    transform: translateY(-12px);
  }
}

.animate-bounce {
  animation: bounce 1.4s ease-in-out infinite;
}

.filter-actions {
  .button-group {
    &:hover {
      button {
        @apply bg-n-brand/10 dark:bg-n-brand/20;
      }
    }
    
    button {
      @apply transition-colors duration-200;
      
      &:hover {
        @apply bg-n-brand/10 dark:bg-n-brand/20;
      }
    }
  }
}
</style>